import { axiosClient } from '../axiosClient';

// O envio espera o Gupshup (até 10 s), e anexo ainda sobe o arquivo antes.
// Com os 10 s padrão do axios a mensagem saía, o painel dizia que falhou e o
// atendente reenviava: duplicada. A chave `client_message_id` cobre o resto.
const TIMEOUT_ENVIO_MS = 60_000;
const TIMEOUT_ARQUIVO_MS = 120_000;
const TIMEOUT_IA_MS = 30_000;

// Nomes de campo herdados do tempo do proxy pro Chatwoot (Conversation,
// ChatwootMessage, etc.) — o backend próprio (Fase 4) devolve esses mesmos
// shapes pra não precisar reescrever a tela inteira. `sender.id`/`chatwootUserId`
// hoje carregam managers.id direto (não existe mais chatwoot_user_id).
export interface ChatwootContact {
  id?: number;
  name?: string;
  phone_number?: string;
  email?: string;
  thumbnail?: string;
}

export interface Conversation {
  id: number;
  status: string;
  unread_count?: number;
  timestamp?: number;
  last_activity_at?: number;
  waiting_since?: number;
  labels?: string[];
  custom_attributes?: {
    pinned?: boolean;
    transfer_from?: string; // nome de quem transferiu (marcador do modal)
    transfer_to?: number; // chatwoot_user_id do destinatário
    transfer_at?: string;
    [k: string]: unknown;
  };
  meta?: { sender?: ChatwootContact; assignee?: { id: number; name: string } | null };
  messages?: ChatwootMessage[];
  last_non_activity_message?: ChatwootMessage;
  [k: string]: unknown;
}

// Atendente disponível pra transferência.
export interface SupportAgent {
  chatwootUserId: number;
  managerId: number;
  name: string;
  email: string;
}

// Etiqueta (flag) da conta no Chatwoot.
export interface ChatwootLabel {
  id: number;
  title: string;
  color?: string;
  description?: string;
}

// Resposta rápida (canned response).
export interface CannedResponse {
  id: number;
  short_code: string;
  content: string;
}

// message_type: 0 incoming (cliente) · 1 outgoing (atendente) · 2 activity · 3 template
// Anexo do WhatsApp (áudio, foto, vídeo, documento). `data_url` é uma URL
// assinada do próprio Chatwoot — o browser carrega direto, sem passar pela API.
export interface ChatwootAttachment {
  id?: number;
  file_type?: 'audio' | 'image' | 'video' | 'file' | string;
  data_url?: string;
  thumb_url?: string;
  /** Mídia que a API não conseguiu guardar (tipo estranho, link vencido). */
  unavailable?: boolean;
  /** Mídia do cliente ainda baixando do Gupshup. */
  pending?: boolean;
  /** Nome original do arquivo. */
  file_name?: string;
  extension?: string;
  content_type?: string;
  file_size?: number;
  transcribed_text?: string;
}

export interface ChatwootMessage {
  id: number;
  content: string | null;
  message_type: number;
  private?: boolean;
  created_at?: number;
  sender?: { id?: number; name?: string; type?: string };
  attachments?: ChatwootAttachment[];
  /**
   * Entrega no WhatsApp: `sent` · `delivered` · `read` · `failed`.
   * O Chatwoot recebe isso da Meta por webhook — é a única forma de saber que
   * uma mensagem nossa não chegou, já que o envio em si responde 200.
   */
  status?: 'sent' | 'delivered' | 'read' | 'failed' | 'progress' | string;
  /** `external_error` traz o motivo da falha de entrega (ou da mídia indisponível). */
  content_attributes?: { external_error?: string; [k: string]: unknown };
  /** O cliente respondeu citando esta mensagem. */
  in_reply_to?: { id: number; content?: string; kind?: string; outgoing?: boolean };
  client_message_id?: string;
  [k: string]: unknown;
}

// Fatias anteriores do mesmo contato. `thread_conversation_id` diz de qual
// atendimento cada mensagem veio — é o que desenha o divisor na timeline.
export interface ThreadHistory {
  conversations: { id: number; status: string; created_at?: number; count: number }[];
  messages: (ChatwootMessage & { thread_conversation_id: number })[];
}

export interface ConversationsResult {
  meta: { open_count?: number; pending_count?: number; snoozed_count?: number };
  conversations: Conversation[];
}

export type AssigneeType = 'all' | 'me' | 'unassigned';

// Card do produtor (nosso banco) casado pelo telefone do contato.
export interface ProducerActivity {
  type: string;
  title: string;
  subtitle?: string;
  at?: string;
}
export interface Producer {
  id: number;
  name: string;
  phone: string;
  status?: string;
  isProducer?: boolean;
  animalCount?: number;
  property?: { name?: string; city?: string; uf?: string } | null;
  assinatura?: {
    status?: string;
    origem?: string;
    plano?: { nome?: string; codigo?: string } | null;
    currentPeriodEnd?: string | null;
  } | null;
  coberturaHerdada?: {
    status?: string;
    produtorNome?: string;
    plano?: { nome?: string; codigo?: string } | null;
  } | null;
  [k: string]: unknown;
}
export interface ProducerResult {
  producer: Producer | null;
  activity: ProducerActivity[];
}

export interface SupportOtp {
  id: number;
  code: string;
  createdAt: string;
  expiresAt: string;
  status: 'active' | 'expired' | 'used';
}

export interface SupportOtpResult {
  otp: SupportOtp | null;
}

export const supportApi = {
  listConversations: (
    params: { status?: string; assignee_type?: AssigneeType; page?: number } = {}
  ) =>
    axiosClient
      .get<ConversationsResult>('/manager/support/conversations', { params })
      .then((r) => r.data),

  // Uma conversa isolada — usado quando um evento de socket (mensagem/status
  // novo) chega e a UI precisa dos dados pra montar a notificação, sem confiar
  // em nada que tenha vindo pelo próprio socket.
  getConversation: (conversationId: number) =>
    axiosClient
      .get<Conversation>(`/manager/support/conversations/${conversationId}`)
      .then((r) => r.data),

  getMessages: (conversationId: number, before?: number) =>
    axiosClient
      .get<{ messages: ChatwootMessage[] }>(
        `/manager/support/conversations/${conversationId}/messages`,
        {
          params: before ? { before } : {},
        }
      )
      .then((r) => r.data.messages),

  // Atendimentos anteriores do mesmo contato, já mesclados e em ordem.
  // A inbox rodou sem `lock_to_single_conversation` por um tempo: quem voltava
  // depois de um encerramento ganhava uma conversa nova. Isto remonta o fio.
  getThreadHistory: (conversationId: number) =>
    axiosClient
      .get<ThreadHistory>(`/manager/support/conversations/${conversationId}/thread`)
      .then((r) => r.data),

  // Marca a conversa como lida (zera o unread_count na listagem).
  markRead: (conversationId: number) =>
    axiosClient.post(`/manager/support/conversations/${conversationId}/read`).then((r) => r.data),

  getProducer: (phone: string) =>
    axiosClient
      .get<ProducerResult>('/manager/support/producer', { params: { phone } })
      .then((r) => r.data),

  getProducerOtp: (phone: string) =>
    axiosClient
      .get<SupportOtpResult>('/manager/support/producer/otp', { params: { phone } })
      .then((r) => r.data),

  sendMessage: (
    conversationId: number,
    content: string,
    isPrivate = false,
    clientMessageId?: string
  ) =>
    axiosClient
      .post<{ message: ChatwootMessage }>(
        `/manager/support/conversations/${conversationId}/messages`,
        {
          content,
          private: isPrivate,
          client_message_id: clientMessageId,
        },
        { timeout: TIMEOUT_ENVIO_MS }
      )
      .then((r) => r.data.message),

  // Áudio gravado no painel. Vai como multipart; a API converte pro formato que
  // o WhatsApp aceita antes de repassar ao Chatwoot.
  sendAudio: (conversationId: number, blob: Blob, filename: string, clientMessageId?: string) => {
    const form = new FormData();
    form.append('audio', blob, filename);
    if (clientMessageId) form.append('client_message_id', clientMessageId);
    return axiosClient
      .post<{ message: ChatwootMessage }>(
        `/manager/support/conversations/${conversationId}/audio`,
        form,
        // O client tem Content-Type: application/json por padrão, e nesse caso o
        // axios serializa o FormData como JSON. Anular deixa o browser montar o
        // multipart com o boundary correto.
        { headers: { 'Content-Type': null }, timeout: TIMEOUT_ARQUIVO_MS }
      )
      .then((r) => r.data.message);
  },

  // Imagem, vídeo ou documento. Mesmo caminho do áudio; a API valida o tipo
  // contra o que o WhatsApp aceita antes de repassar ao Chatwoot.
  sendAttachment: (
    conversationId: number,
    file: File,
    caption = '',
    clientMessageId?: string,
    trecho?: { inicio: number; fim: number } | null
  ) => {
    const form = new FormData();
    form.append('file', file, file.name);
    if (caption) form.append('caption', caption);
    // Trecho do vídeo escolhido no editor: o servidor corta e converte pra MP4.
    if (trecho) {
      form.append('trim_start', trecho.inicio.toFixed(2));
      form.append('trim_end', trecho.fim.toFixed(2));
    }
    if (clientMessageId) form.append('client_message_id', clientMessageId);
    return axiosClient
      .post<{
        message: ChatwootMessage;
      }>(`/manager/support/conversations/${conversationId}/attachment`, form, {
        headers: { 'Content-Type': null },
        timeout: TIMEOUT_ARQUIVO_MS,
      })
      .then((r) => r.data.message);
  },

  // Arquivo de uma mensagem, pela API (o domínio público das mídias não manda
  // CORS, então o navegador não salvaria com o nome original sozinho).
  downloadMedia: (messageId: number) =>
    axiosClient
      .get<Blob>(`/manager/support/messages/${messageId}/media`, {
        responseType: 'blob',
        timeout: TIMEOUT_ARQUIVO_MS,
      })
      .then((r) => r.data),

  // Mostra "digitando" no WhatsApp do cliente e marca as mensagens dele como
  // lidas. Falha aqui é irrelevante — nunca deve incomodar quem está digitando.
  setTyping: (conversationId: number, typing = true) =>
    axiosClient
      .post(`/manager/support/conversations/${conversationId}/typing`, { typing })
      .then((r) => r.data)
      .catch(() => ({ ok: false })),

  toggleStatus: (conversationId: number, status: 'open' | 'resolved' | 'pending' | 'snoozed') =>
    axiosClient
      .post(`/manager/support/conversations/${conversationId}/toggle_status`, { status })
      .then((r) => r.data),

  // unassign=true desatribui; senão assume a conversa (atribui a mim).
  assign: (conversationId: number, unassign = false) =>
    axiosClient
      .post(`/manager/support/conversations/${conversationId}/assignments`, { unassign })
      .then((r) => r.data),

  // Transfere a conversa pra outro atendente.
  transfer: (conversationId: number, assigneeId: number) =>
    axiosClient
      .post(`/manager/support/conversations/${conversationId}/transfer`, {
        assignee_id: assigneeId,
      })
      .then((r) => r.data),

  // Aceita uma transferência (destinatário clica "Assumir" no modal): assume,
  // limpa o marcador e dispara a boas-vindas.
  acceptTransfer: (conversationId: number) =>
    axiosClient
      .post(`/manager/support/conversations/${conversationId}/accept-transfer`)
      .then((r) => r.data),

  // Atendentes disponíveis pra transferência.
  listAgents: () =>
    axiosClient
      .get<{ agents: SupportAgent[] }>('/manager/support/agents')
      .then((r) => r.data.agents),

  // Etiquetas (flags) disponíveis na conta.
  listLabels: () =>
    axiosClient
      .get<{ labels: ChatwootLabel[] }>('/manager/support/labels')
      .then((r) => r.data.labels),

  // Define a lista completa de etiquetas de uma conversa.
  setLabels: (conversationId: number, labels: string[]) =>
    axiosClient
      .post(`/manager/support/conversations/${conversationId}/labels`, { labels })
      .then((r) => r.data),

  // Fixa/desfixa a conversa (destaque no topo).
  pin: (conversationId: number, pinned: boolean) =>
    axiosClient
      .post(`/manager/support/conversations/${conversationId}/pin`, { pinned })
      .then((r) => r.data),

  // Respostas rápidas (canned responses).
  listCanned: () =>
    axiosClient
      .get<{ canned: CannedResponse[] }>('/manager/support/canned')
      .then((r) => r.data.canned),

  // Corrige ortografia/gramática do rascunho. Não manda nada ao cliente.
  correctDraft: (draft: string) =>
    axiosClient
      .post<{
        corrected: string;
      }>('/manager/support/ai/correct', { draft }, { timeout: TIMEOUT_IA_MS })
      .then((r) => r.data.corrected),

  // Sugere a próxima resposta com base no histórico da conversa.
  suggestReply: (conversationId: number) =>
    axiosClient
      .post<{
        suggestion: string;
      }>(`/manager/support/conversations/${conversationId}/ai/suggest`, undefined, {
        timeout: TIMEOUT_IA_MS,
      })
      .then((r) => r.data.suggestion),
};

// Mensagem de erro que dá pra mostrar ao atendente, venha de onde vier.
export function mensagemDeErro(err: unknown, padrao = 'Algo deu errado. Tente de novo.'): string {
  const e = err as {
    code?: string;
    message?: string;
    response?: {
      status?: number;
      data?: { error?: { message?: string } | string; message?: string };
    };
  } | null;
  if (!e) return padrao;
  const data = e.response?.data;
  const daApi = typeof data?.error === 'object' ? data.error?.message : data?.message;
  if (daApi) return daApi;
  if (e.code === 'ECONNABORTED')
    return 'Demorou demais para responder. Confira a conversa antes de tentar de novo.';
  if (!e.response) return 'Sem conexão com o servidor. Confira a internet e tente de novo.';
  return padrao;
}

export function codigoDeErro(err: unknown): string | null {
  const data = (err as { response?: { data?: { error?: { code?: string } } } } | null)?.response
    ?.data;
  return data?.error?.code ?? null;
}
